import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, imgUrl } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const DraftRow = ({ d, text, setText, current, onDone }) => {
  const approve = async () => {
    try {
      const { data } = await api.post(`/admin/ai/drafts/${d.slug}/approve`, { text });
      onDone(d.slug, data.description);
      toast.success(`${d.name} description saved`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const discard = async () => {
    try { await api.delete(`/admin/ai/drafts/${d.slug}`); onDone(d.slug, null); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <li data-testid={ADMIN.draftRow(d.slug)} className="py-5 grid md:grid-cols-[64px_1fr_auto] gap-4">
      <img src={imgUrl(d.image)} alt="" className="w-16 h-16 rounded-xl object-cover" />
      <div className="min-w-0">
        <p className="font-semibold">{d.name}</p>
        <p className="text-xs text-stone-500 mt-1">Now: {current || "No description"}</p>
        <textarea data-testid={ADMIN.draftText(d.slug)} value={text} onChange={(e) => setText(e.target.value)} rows={4} className="w-full mt-2 px-3 py-2 rounded-xl border border-[#E7E0D3] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E06D44]/40" />
      </div>
      <div className="flex md:flex-col gap-2">
        <button data-testid={ADMIN.draftApprove(d.slug)} disabled={!text.trim()} onClick={approve} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1C1917] text-white text-sm hover:bg-[#E06D44] disabled:opacity-30 transition-colors"><Check size={14} /> Approve</button>
        <button data-testid={ADMIN.draftDiscard(d.slug)} onClick={discard} className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#E7E0D3] text-sm hover:bg-[#F0EBE1] transition-colors"><Trash2 size={14} /> Discard</button>
      </div>
    </li>
  );
};

export const AdminDrafts = ({ products, onProductUpdated }) => {
  const [state, setState] = useState(null);
  const [edits, setEdits] = useState({});
  const load = useCallback(() => api.get("/admin/ai/drafts").then(({ data }) => setState(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const running = state?.job?.running;
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [running, load]);

  const start = async () => {
    try {
      const { data } = await api.post("/admin/ai/drafts/run");
      toast.success(data.total ? `Claude is drafting ${data.total} descriptions` : "Nothing new to draft");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const done = (slug, description) => {
    setState((s) => ({ ...s, drafts: s.drafts.filter((d) => d.slug !== slug) }));
    if (description != null) onProductUpdated(slug, { description });
  };
  const approveAll = async () => {
    let failed = 0;
    for (const d of state.drafts) {
      try {
        const { data } = await api.post(`/admin/ai/drafts/${d.slug}/approve`, { text: edits[d.slug] ?? d.text });
        done(d.slug, data.description);
      } catch (e) { failed += 1; toast.error(`${d.name}: ${errMsg(e)}`); }
    }
    if (!failed) toast.success("All drafts approved");
  };

  if (!state) return null;
  const current = Object.fromEntries(products.map((p) => [p.slug, p.description]));
  return (
    <section className="mt-6">
      <div className="flex flex-wrap items-center gap-3">
        <button data-testid={ADMIN.draftsRun} disabled={running || !state.missing} onClick={start} className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#E06D44] text-white text-sm font-semibold hover:bg-[#C95B33] disabled:opacity-40 transition-colors">
          {running ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Draft descriptions ({state.missing} missing or short{state.drafts.length ? `, ${state.drafts.length} awaiting review` : ""})
        </button>
        {running && <span data-testid={ADMIN.draftsProgress} className="text-sm text-stone-600">Writing {state.job.done} of {state.job.total}…</span>}
        {!running && state.job?.failed > 0 && <span className="text-sm text-amber-700">{state.job.failed} couldn't be drafted. Run again to retry.</span>}
        {state.drafts.length > 1 && <button data-testid={ADMIN.draftsApproveAll} onClick={approveAll} className="ml-auto px-4 py-2 rounded-full border border-[#1C1917] text-sm hover:bg-[#1C1917] hover:text-white transition-colors">Approve all {state.drafts.length}</button>}
      </div>
      <p className="mt-2 text-xs text-stone-500">Claude rewrites descriptions that are empty or under 120 characters, using each product's specs. Nothing changes on the shop until you approve.</p>
      {state.drafts.length ? (
        <ul className="mt-4 divide-y divide-[#F0EBE1]">{state.drafts.map((d) => <DraftRow key={d.slug} d={d} text={edits[d.slug] ?? d.text} setText={(t) => setEdits((m) => ({ ...m, [d.slug]: t }))} current={current[d.slug]} onDone={done} />)}</ul>
      ) : (
        <p data-testid={ADMIN.draftsEmpty} className="mt-6 text-sm text-stone-500">{running ? "Drafts will appear here as Claude finishes them." : "No drafts waiting for review."}</p>
      )}
    </section>
  );
};
